import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENDPOINTS } from 'app/core/constants/endpoints';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { ApiService } from 'app/core/services/api.service';
import {
  UpdateProfileRequest,
  UpdateProfileResponse,
} from 'app/modules/home/models/admin';
import { BaseResponse, CategoryModel } from 'app/shared/models';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  AdminAuthResponse,
  AdminLoginData,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminRefreshResponse,
  AdminUser,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ErrorHandlerService {
  private readonly TOKEN_KEY = 'token';
  private readonly USER_KEY = 'user';

  private currentUserSubject = new BehaviorSubject<AdminUser | null>(
    this.getCurrentUser()
  );
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasValidToken()
  );
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private dataStorageService: DataStorageService
  ) {
    super();
  }

  login(credentials: AdminLoginRequest): Observable<AdminLoginResponse> {
    return this.apiService
      .post<AdminLoginResponse>(
        ENDPOINTS.ADMIN_AUTH.LOGIN,
        credentials,
        new HttpContext().set(SKIP_AUTH_REFRESH, true)
      )
      .pipe(
        tap((response) => {
          if (response.status.success) {
            this.storeAuthData(response.data);
          }
        })
      );
  }

  logout(): Observable<any> {
    return this.apiService.post(ENDPOINTS.ADMIN_AUTH.LOGOUT, {}).pipe(
      tap(() => {
        this.clearAuthData();
      })
    );
  }

  /**
   * * Set Password for First Time
   * @param token string
   * @param password string
   * @param password_confirmation string
   * @returns Observable<BaseResponse>
   */
  setPassword(
    token: string,
    password: string,
    password_confirmation: string
  ): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(
      ENDPOINTS.ADMIN_AUTH.SET_PASSWORD,
      {
        token: token,
        password: password,
        password_confirmation: password_confirmation,
      },
      new HttpContext().set(SKIP_AUTH_REFRESH, true)
    );
  }

  getCategories(): CategoryModel[] {
    const categories = sessionStorage.getItem('categories');
    if (categories) {
      return JSON.parse(categories);
    } else {
      const categories = this.dataStorageService.get('categories');
      return categories ? JSON.parse(categories) : [];
    }
  }

  getCurrentAuthenticatedUser(): Observable<AdminAuthResponse> {
    return this.apiService.get<AdminAuthResponse>(ENDPOINTS.ADMIN_AUTH.ME).pipe(
      tap((response) => {
        if (response.status.success) {
          this.updateCurrentUser(response.data);
        }
      })
    );
  }

  refreshToken(): Observable<AdminRefreshResponse> {
    return this.apiService
      .post<AdminRefreshResponse>(
        ENDPOINTS.ADMIN_AUTH.REFRESH,
        {},
        new HttpContext().set(SKIP_AUTH_REFRESH, true)
      )
      .pipe(
        tap((response) => {
          if (response.status.success) {
            this.storeToken(response.data.token);
          }
        })
      );
  }

  updateProfile(
    request: UpdateProfileRequest
  ): Observable<UpdateProfileResponse> {
    const formData = new FormData();

    if (request.first_name) {
      formData.append('first_name', request.first_name);
    }
    if (request.last_name) {
      formData.append('last_name', request.last_name);
    }
    if (request.email) {
      formData.append('email', request.email);
    }
    if (request.phone_number) {
      formData.append('phone_number', request.phone_number);
    }
    if (request.avatar) {
      formData.append('avatar', request.avatar);
    }

    return this.apiService
      .post<UpdateProfileResponse>(
        ENDPOINTS.ADMIN_AUTH.UPDATE_PROFILE,
        formData
      )
      .pipe(
        tap((response) => {
          if (response.status.success) {
            this.updateCurrentUser(response.data);
          }
        })
      );
  }

  private storeAuthData(data: AdminLoginData): void {
    this.dataStorageService.set(this.TOKEN_KEY, data.access_token);
    this.dataStorageService.set(this.USER_KEY, JSON.stringify(data.user));
    this.currentUserSubject.next(data.user);
    this.isAuthenticatedSubject.next(true);
  }

  private storeToken(token: string): void {
    this.dataStorageService.set(this.TOKEN_KEY, token);
  }

  private updateCurrentUser(user: AdminUser): void {
    this.dataStorageService.set(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private clearAuthData(): void {
    this.dataStorageService.remove(this.TOKEN_KEY);
    this.dataStorageService.remove(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  clearSession(): void {
    sessionStorage.clear();
    this.dataStorageService.clearAll();
  }

  getToken(): string | null {
    return this.dataStorageService.get(this.TOKEN_KEY);
  }

  getCurrentUser(): AdminUser {
    const user = sessionStorage.getItem(this.USER_KEY);
    if (user) {
      return JSON.parse(user);
    } else {
      const user = this.dataStorageService.get(this.USER_KEY);
      return user ? JSON.parse(user) : ({} as AdminUser);
    }
  }

  hasValidToken(): boolean {
    return !!this.getToken();
  }

  isAuthenticated(): boolean {
    return this.hasValidToken();
  }
}
